import { Component } from '@theme/component';

/**
 * Tabs custom element that manages tab navigation and content display.
 *
 * This component moves tab buttons from tab-item elements to the tabs header
 * and manages the active state of tabs and panels.
 *
 * @extends Component
 */
export class TabsCustom extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Initialize tabs after a brief delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.initializeTabs();
    });
  }

  /**
   * Initialize tabs by moving buttons to header and setting up event listeners
   */
  initializeTabs() {
    const header = this.querySelector('.tabs__header');
    const tabItems = this.querySelectorAll('tab-item');
    let hasOpenByDefault = false;

    if (!header) return;

    // Move buttons to header
    tabItems.forEach((tabItem, index) => {
      const button = tabItem.querySelector('[data-tab-button]');
      const panel = tabItem.querySelector('[data-tab-panel]');

      if (!button || !panel) return;

      // Move button to header
      header.appendChild(button);

      // Add click event listener
      button.addEventListener('click', () => this.activateTab(index));

      // Check if this tab should be open by default
      if (tabItem.hasAttribute('data-open-by-default') && !hasOpenByDefault) {
        hasOpenByDefault = true;
        this.activateTab(index);
      }
    });

    header.classList.remove('loading');

    // If no tab is open by default, open the first one
    if (!hasOpenByDefault && tabItems.length > 0) {
      this.activateTab(0);
    }
  }

  /**
   * Activate a specific tab by index
   * @param {number} index - The index of the tab to activate
   */
  activateTab(index) {
    const header = this.querySelector('.tabs__header');
    const content = this.querySelector('.tabs__content');

    if (!header || !content) return;

    const buttons = header.querySelectorAll('[data-tab-button]');
    const panels = content.querySelectorAll('[data-tab-panel]');

    // Deactivate all tabs
    buttons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });

    panels.forEach(panel => {
      panel.classList.remove('active');
    });

    // Activate selected tab
    if (buttons[index] && panels[index]) {
      buttons[index].classList.add('active');
      buttons[index].setAttribute('aria-selected', 'true');
      panels[index].classList.add('active');
    }
  }
}

customElements.define('tabs-custom', TabsCustom);